import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import { cn } from "@/shared/lib/cn";

const buttonVariants = cva(
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dark_green/40 dark:focus-visible:ring-green_on_dark/40 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-night disabled:pointer-events-none disabled:opacity-50 select-none",
    {
        variants: {
            variant: {
                default:
                    "bg-dark_green text-white hover:bg-dark_green-700 shadow-sm active:translate-y-[1px]",
                destructive:
                    "bg-red text-white hover:bg-red-600 shadow-sm active:translate-y-[1px]",
                outline:
                    "border border-slate-300 dark:border-zinc-700 bg-white dark:bg-night_input text-slate-800 dark:text-zinc-200 hover:bg-slate-50 dark:hover:bg-night_surface hover:border-dark_green dark:hover:border-green_on_dark hover:text-dark_green dark:hover:text-green_on_dark shadow-sm",
                secondary:
                    "bg-slate-100 text-slate-900 hover:bg-slate-200 dark:bg-zinc-800 dark:text-zinc-100 dark:hover:bg-zinc-700 shadow-sm",
                ghost:
                    "text-slate-700 dark:text-zinc-300 hover:bg-slate-100 dark:hover:bg-night_input hover:text-slate-900 dark:hover:text-white",
                link:
                    "text-dark_green dark:text-green_on_dark underline-offset-4 hover:underline p-0 h-auto font-normal",
                accent:
                    "bg-yellow text-black hover:bg-yellow-400 shadow-sm active:translate-y-[1px] font-semibold",
            },
            size: {
                // Em telas de toque todo tamanho sobe para o alvo mínimo de 44px.
                default: "h-10 touch:h-11 px-4 py-2",
                sm: "h-8 touch:h-10 rounded-md px-3 text-xs",
                lg: "h-11 rounded-lg px-8 text-base",
                icon: "h-10 w-10 touch:h-11 touch:w-11 p-0",
                "icon-sm": "h-8 w-8 touch:h-10 touch:w-10 p-0 text-xs",
            },
        },
        defaultVariants: {
            variant: "default",
            size: "default",
        },
    }
);

export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>,
        VariantProps<typeof buttonVariants> {
    asChild?: boolean;
    isLoading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    ({ className, variant, size, asChild = false, isLoading = false, disabled, children, ...props }, ref) => {
        if (asChild) {
            return (
                <Slot
                    className={cn(buttonVariants({ variant, size, className }))}
                    ref={ref}
                    {...props}
                >
                    {children}
                </Slot>
            );
        }

        return (
            <button
                className={cn(buttonVariants({ variant, size, className }))}
                ref={ref}
                disabled={disabled || isLoading}
                {...props}
            >
                {isLoading && <Loader2 className="h-4 w-4 animate-spin text-current" />}
                {children}
            </button>
        );
    }
);
Button.displayName = "Button";

export { Button, buttonVariants };
